import { useState, useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faDownload,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

export default function AthleteContactModal({ attendees, eventName, onClose }) {
  const [search, setSearch] = useState("");

  const displayValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "null"
    ) {
      return "-";
    }

    return value;
  };

  const columns = [
    { header: "Name", accessor: (p) => `${p.name} ${p.lastname}` },
    { header: "Position", accessor: (p) => p.position },
    { header: "Jersey", accessor: (p) => p.jerseyNumber },
    { header: "T-Shirt", accessor: (p) => p.tShirtSize },
    { header: "Athlete Email", accessor: (p) => p.athleteEmail },
    { header: "Parent Email", accessor: (p) => p.parentEmail },
    { header: "Athlete Phone", accessor: (p) => p.athletePhone },
    { header: "Parent Phone", accessor: (p) => p.parentPhone },
    { header: "X (Twitter)", accessor: (p) => p.twitter },
    { header: "Instagram", accessor: (p) => p.instagram },
    {
      header: "Drills",
      accessor: (p) => (p.hasPositionDrills ? "Yes" : "No"),
    },
  ];

  const filteredData = useMemo(() => {
    return attendees?.filter((p) =>
      `${p.name} ${p.lastname}`.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, attendees]);

  const exportCSV = () => {
    if (!filteredData?.length) return;

    const headers = columns.map((c) => c.header).join(",");

    const rows = filteredData
      .map((row) => columns.map((c) => `"${c.accessor(row) ?? ""}"`).join(","))
      .join("\n");

    const blob = new Blob([`${headers}\n${rows}`], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "athlete-contact-info.csv";
    link.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-7xl mx-2 rounded-lg shadow-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between p-4 border-b gap-2">
          <div>
            <h2 className="text-lg font-semibold">
              Athlete Contact Information
            </h2>
            <p className="text-sm text-gray-500">{eventName}</p>
          </div>

          <div className="flex gap-3">
            <div className="relative">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                placeholder="Search athlete name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 border rounded-md text-sm hover:bg-gray-200 whitespace-nowrap mr-5"
            >
              <FontAwesomeIcon icon={faDownload} className="text-gray-600" />
              Export CSV
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-black text-lg ml-0"
            >
              ✕
            </button>
          </div>
        </div>
        {/* Table (Desktop) */}
        <div className="hidden md:block overflow-x-auto h-[500px] p-5">
          <div className=" rounded-lg border border-gray-200 overflow-x-scroll">
            <table className="w-full text-sm border rounded-md">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {columns.map((col) => (
                    <th key={col.header} className="p-3 text-left">
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((p, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-3 font-medium">
                      {p.name} {p.lastname}
                    </td>
                    <td className="p-3">{p.position}</td>
                    <td className="p-3 text-center">{p.jerseyNumber}</td>
                    <td className="p-3 text-center">{p.tShirtSize}</td>
                    <td className="p-3 text-blue-600">{p.athleteEmail}</td>
                    <td className="p-3 text-blue-600">{p.parentEmail}</td>
                    <td className="p-3">{p.athletePhone}</td>
                    <td className="p-3">{p.parentPhone}</td>
                    <td className="p-3">{displayValue(p.twitter)}</td>
                    <td className="p-3">{displayValue(p.instagram)}</td>
                    <td className="p-3 text-center">
                      {p.hasPositionDrills && (
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="text-green-500 text-lg"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden p-3 space-y-3 overflow-y-auto h-[460px]">
          {filteredData.map((p, i) => (
            <div key={i} className="border rounded-lg p-3 space-y-1">
              <div className="font-semibold">
                {p.name} {p.lastname}
              </div>
              <div className="text-sm text-gray-500">
                Position: {p.position}
              </div>
              <div className="text-sm">
                Jersey: {displayValue(p.jerseyNumber)}
              </div>
              <div className="text-sm">
                T-Shirt: {displayValue(p.tShirtSize)}
              </div>
              <div className="text-sm text-blue-600">
                Athlete Email: {displayValue(p.athleteEmail)}
              </div>
              <div className="text-sm text-blue-600">
                Parent Email: {displayValue(p.parentEmail)}
              </div>
              <div className="text-sm">
                Athlete Phone: {displayValue(p.athletePhone)}
              </div>
              <div className="text-sm">
                Parent Phone: {displayValue(p.parentPhone)}
              </div>
              <div className="text-sm">
                X (Twitter): {displayValue(p.twitter)}
              </div>
              <div className="text-sm">
                Instagram: {displayValue(p.instagram)}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Drills:</span>
                {p.hasPositionDrills ? (
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className="text-green-500"
                  />
                ) : (
                  <span className="text-gray-400">No</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
